import styled, { css } from 'styled-components';
import { ReactNode } from 'react';
import { Container } from './Container';

type SectionSpacing = 'sm' | 'md' | 'lg' | 'xl';

interface SectionProps {
  children: ReactNode;
  id?: string;
  spacing?: SectionSpacing;
  background?: 'default' | 'secondary' | 'gradient';
  className?: string;
  containerSize?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
  noContainer?: boolean;
}

const spacingMap = {
  sm: css`
    padding: var(--space-12) 0;

    @media (max-width: 768px) {
      padding: var(--space-8) 0;
    }
  `,
  md: css`
    padding: var(--space-16) 0;

    @media (max-width: 768px) {
      padding: var(--space-12) 0;
    }
  `,
  lg: css`
    padding: var(--space-20) 0;

    @media (max-width: 768px) {
      padding: var(--space-16) 0;
    }
  `,
  xl: css`
    padding: var(--space-24) 0;

    @media (max-width: 768px) {
      padding: var(--space-20) 0;
    }
  `,
};

const backgroundStyles = {
  default: css`
    background: transparent;
  `,
  secondary: css`
    background: var(--color-background-secondary);
  `,
  gradient: css`
    background: linear-gradient(
      180deg,
      var(--color-background) 0%,
      var(--color-background-secondary) 100%
    );
  `,
};

const StyledSection = styled.section<{
  $spacing: SectionSpacing;
  $background: 'default' | 'secondary' | 'gradient';
}>`
  ${props => spacingMap[props.$spacing]}
  ${props => backgroundStyles[props.$background]}
`;

export function Section({
  children,
  id,
  spacing = 'lg',
  background = 'default',
  className,
  containerSize = 'xl',
  noContainer = false,
}: SectionProps) {
  return (
    <StyledSection
      id={id}
      $spacing={spacing}
      $background={background}
      className={className}
    >
      {noContainer ? children : (
        <Container size={containerSize}>
          {children}
        </Container>
      )}
    </StyledSection>
  );
}

// Section Header component for consistent titles
interface SectionHeaderProps {
  title: string;
  subtitle?: string;
  centered?: boolean;
}

const HeaderWrapper = styled.div<{ $centered: boolean }>`
  margin-bottom: var(--space-12);

  ${props => props.$centered && css`
    text-align: center;
  `}
`;

const Title = styled.h2`
  margin-bottom: var(--space-4);
`;

const Subtitle = styled.p`
  font-size: var(--text-lg);
  max-width: 600px;

  ${HeaderWrapper}[data-centered="true"] & {
    margin: 0 auto;
  }
`;

export function SectionHeader({ title, subtitle, centered = false }: SectionHeaderProps) {
  return (
    <HeaderWrapper $centered={centered} data-centered={centered}>
      <Title>{title}</Title>
      {subtitle && <Subtitle>{subtitle}</Subtitle>}
    </HeaderWrapper>
  );
}
